/**
 * English - `emailDesign`: the visual editor for the emails a shop sends.
 *
 * Its own namespace rather than more `comms` keys, because it is its own
 * screen: `comms` is the list of emails and the wording dialog, and this is
 * the canvas, the palette and the block inspector that open on top of them.
 *
 * Keys: `block.*` names a kind of block and is what the palette shows;
 * `empty.*` is what an unfilled block says on the canvas; `group.*` are the
 * inspector's section headings; `box.*`, `type.*` and `font.*` are the two
 * shared panels every block uses; everything under a block's own name belongs
 * to that block's inspector. `preview.*` is the desktop/mobile/source dialog,
 * and `note.*` mirrors the advisory codes in
 * `app/services/comms/email_html.py` - a code with no key here falls back to
 * the English the server sent.
 */
const emailDesign: Record<string, string> = {
  'title': 'Design: {name}',
  'subtitle': 'Click a block to change it. Preview shows the email as it will arrive.',
  'done': 'Done',
  'undo': 'Undo',
  'redo': 'Redo',

  'designed': 'Designed email',
  'use_plain_text': 'Use plain text instead',
  'edit_design': 'Edit the design',
  'view_design': 'View the design',
  'offer':
    'Prefer something with your logo, your colours and a button? Designing this email lays out the wording above — nothing is lost, and you can go back to plain text at any time.',
  'start': 'Design this email',

  'missing_required':
    'This email cannot be saved without {names}. Without it the message is a dead end for the person who asked for it.',
  'block_save_failed': 'That block could not be saved.',

  'canvas_empty': 'Nothing here yet. Add a block to begin.',
  'select_block': 'Select the {name} block',
  'move_up': 'Move up',
  'move_down': 'Move down',
  'duplicate': 'Duplicate',
  'keep': 'Keep this {name} to use again',
  'remove': 'Remove',

  'keep_title': 'Keep this block',
  'keep_hint':
    'It appears under "Your blocks" in every email. Using it copies it, so changing the saved one later does not change emails already built with it.',
  'keep_name': 'Call it',
  'keep_confirm': 'Keep it',

  'pane.label': 'Which panel to show',
  'pane.canvas': 'Email',
  'pane.add': 'Add',
  'pane.edit': 'Settings',
  'palette.title': 'Add a block',
  'palette.saved': 'Your blocks',
  'palette.saved_empty':
    'Nothing kept yet. Save a block from the canvas and it appears here, ready for your other emails.',
  'palette.forget': 'Forget {name}',

  'settings.title': 'This email',
  'settings.open': 'Email settings',
  'settings.preheader': 'Preview line',
  'settings.preheader_hint':
    'The line the inbox shows under the subject. Leave it empty and the inbox uses whatever the email happens to start with.',
  'settings.font': 'Typeface',
  'settings.font_hint': 'Used by every block that has not chosen its own.',
  'settings.text_colour': 'Text colour',
  'settings.link_colour': 'Link colour',
  'settings.page_background': 'Page background',
  'settings.page_background_hint': 'Behind the email, edge to edge.',
  'settings.content_background': 'Email background',
  'settings.width': 'Width',
  'settings.corners': 'Rounded corners',
  'settings.width_hint':
    '600 pixels is what every email app shows without a sideways scrollbar. Narrower reads as a letter; wider is a gamble.',

  'block.text': 'Text',
  'block.heading': 'Heading',
  'block.image': 'Image',
  'block.button': 'Button',
  'block.divider': 'Divider',
  'block.spacer': 'Space',
  'block.list': 'List',
  'block.columns': 'Columns',
  'block.banner': 'Banner',
  'block.social': 'Social links',
  'block.header': 'Header',
  'block.footer': 'Footer',

  'empty.words': 'Your words here',
  'empty.heading': 'Heading',
  'empty.image': 'No picture chosen',
  'empty.list': 'List item',
  'empty.footer': 'Footer',
  'empty.button': 'Button',
  'empty.banner': 'Banner',

  'align.left': 'Align left',
  'align.center': 'Align centre',
  'align.right': 'Align right',

  'group.content': 'Content',
  'group.style': 'Style',
  'group.spacing': 'Spacing',
  'group.typeface': 'Typeface',

  'box.space_above': 'Space above',
  'box.space_below': 'Space below',
  'box.space_sides': 'Space at the sides',
  'box.background': 'Background',
  'box.background_hint': 'Behind this block only.',
  'box.border': 'Border',
  'box.border_colour': 'Border colour',
  'box.corners': 'Rounded corners',

  'type.font': 'Typeface',
  'type.font_hint': 'Leave as the email’s own unless this block should stand apart.',
  'type.font_inherit': 'Same as the email',
  'type.size': 'Size',
  'type.colour': 'Colour',
  'type.colour_hint': 'Leave unset to use the email’s text colour.',
  'type.align': 'Alignment',
  'type.line_height': 'Line spacing',
  'type.bold': 'Bold',
  'type.italic': 'Italic',

  'font.system': 'System (recommended)',
  'font.helvetica': 'Helvetica',
  'font.arial': 'Arial',
  'font.georgia': 'Georgia',
  'font.times': 'Times New Roman',
  'font.trebuchet': 'Trebuchet',
  'font.verdana': 'Verdana',
  'font.courier': 'Courier',

  'text.words': 'Text',
  'text.placeholder': 'Write what this part of the email says…',

  'heading.text': 'Heading',
  'heading.level': 'Importance',
  'heading.level_hint':
    'Screen readers announce the difference, and someone skimming with one is exactly who a heading helps.',
  'heading.level_1': 'Main heading',
  'heading.level_2': 'Section heading',
  'heading.level_3': 'Small heading',

  'button.label': 'Button text',
  'button.link': 'Where it goes',
  'button.link_hint':
    'A full https:// address, or a placeholder such as {{order_url}} that we fill in when the email is sent.',
  'button.background': 'Button colour',
  'button.text_colour': 'Text colour',
  'button.size': 'Size',
  'button.corners': 'Rounded corners',
  'button.align': 'Alignment',
  'button.full_width': 'Stretch across the email',
  'button.full_width_hint': 'Easier to press on a phone; heavier on a wide screen.',

  'size.small': 'Small',
  'size.medium': 'Medium',
  'size.large': 'Large',

  'image.picture': 'Picture',
  'image.link': 'Where it goes when pressed',
  'image.link_hint': 'Optional. A full https:// address, or a placeholder.',
  'image.width': 'Width',
  'image.align': 'Alignment',
  'image.corners': 'Rounded corners',
  'image.address': 'Address',
  'image.address_hint':
    'Must start with https://. Email apps block insecure pictures and show the warning instead.',
  'image.alt': 'Description',
  'image.alt_hint':
    'What the picture shows. Around a third of people read email with pictures switched off, and they see this instead.',
  'image.choose': 'Choose a picture',
  'image.uploading': 'Uploading…',
  'image.remove': 'Remove',
  'image.too_large': 'That file is {size} MB. Choose one under 8 MB.',
  'image.optimised': 'Stored at {kb} KB, in a format every email app can show.',
  'image.not_optimised':
    'Stored as you uploaded it — we could not make a smaller copy of this format, so it may be slow on a phone.',
  'image.upload_failed': 'That picture could not be uploaded. Please try again.',
  'image.insecure': 'Use an https:// address. Email apps block http:// pictures.',

  'divider.colour': 'Colour',
  'divider.thickness': 'Thickness',
  'divider.style': 'Style',
  'divider.width': 'Width',

  'line.solid': 'Solid',
  'line.dashed': 'Dashed',
  'line.dotted': 'Dotted',

  'spacer.height': 'Height',

  'list.numbered': 'Numbered',
  'list.item': 'Item {number}',
  'list.remove': 'Remove item {number}',
  'list.add': 'Add an item',

  'social.remove': 'Remove {name}',
  'social.platform': 'Where',
  'social.url': 'Address',
  'social.add': 'Add a link',
  'social.style': 'Shown as',
  'social.style_hint':
    'Tiles are drawn by us, so they appear even when someone has pictures switched off.',
  'social.tiles': 'Coloured tiles',
  'social.text': 'Text links',
  'social.size': 'Size',
  'social.align': 'Alignment',
  'social.tile_colour': 'Tile colour',
  'social.tile_colour_hint': 'Leave unset to use each service’s own colour.',

  'banner.heading': 'Heading',
  'banner.text': 'Text',
  'banner.button_label': 'Button text',
  'banner.button_link': 'Where the button goes',
  'banner.background': 'Background colour',
  'banner.background_hint':
    'What the panel looks like when the picture does not load — which is often, so choose one that the text reads well on.',
  'banner.background_image': 'Background picture',
  'banner.background_image_hint': 'Optional. The colour above still shows through where it fails.',
  'banner.text_colour': 'Text colour',
  'banner.button_background': 'Button colour',
  'banner.button_colour': 'Button text colour',
  'banner.height': 'Height',
  'banner.align': 'Alignment',

  'header.logo': 'Logo',
  'header.logo_hint': 'Usually your shop’s. Leave it empty to show the shop’s name instead.',
  'header.title': 'Name',
  'header.title_hint': 'Shown when there is no logo. Leave empty to use your shop’s name.',
  'header.tagline': 'Tagline',
  'header.logo_width': 'Logo width',
  'header.align': 'Alignment',

  'footer.text': 'Text',
  'footer.text_hint': 'Your address, or a line about who this email is from.',
  'footer.link_label': 'Link {number} text',
  'footer.link_url': 'Link {number} address',
  'footer.remove_link': 'Remove link {number}',
  'footer.add_link': 'Add a link',
  'footer.link_colour': 'Link colour',

  'columns.hint':
    'Click a block inside a column to change it. On a phone the columns stack one above the other.',
  'columns.even': 'Equal',
  'columns.wide_left': 'Wider on the left',
  'columns.wide_right': 'Wider on the right',
  'columns.gap': 'Gap between',
  'columns.stack': 'Stack on a phone',
  'columns.stack_hint': 'Off means two narrow columns on a small screen, which is rarely readable.',

  'preview.desktop': 'Desktop',
  'preview.mobile': 'Phone',
  'preview.text': 'Plain text',
  'preview.source': 'Source',
  'preview.copy': 'Copy the HTML',
  'preview.copied': 'Copied',
  'preview.weight': '{kb} KB',
  'preview.frame': 'The email, {view}',

  'note.IMAGE_WITHOUT_ALT':
    'A picture has no description. Around a third of people read email with pictures switched off, and they see the description instead.',
  'note.LOW_CONTRAST':
    'The text and its background are close in tone. Aim for a clear difference so it stays readable on a phone in daylight.',
  'note.MANY_IMAGES':
    'This email asks for a lot of pictures. Each one is a separate request on a phone, and a heavily pictorial email is more likely to be filtered.',
  'note.TOO_LARGE':
    'This email is over 100 KB. Gmail hides anything past that behind a “view entire message” link, which most people do not press.',
  'note.BUTTON_WITHOUT_LINK': 'A button has no address, so pressing it does nothing.',
  'note.NO_PREHEADER':
    'No preview line is set, so the inbox shows whatever the email happens to start with.',
};

export default emailDesign;
